import { IconButton } from "@fluentui/react";
import { getNextColor, IChartProps } from "@fluentui/react-charting";
import React, { useEffect, useState } from "react";
import { LineChartComponent } from "../../../../ChartComponents/LineChartFluent";
import { iconButtonStyles } from "../../../../SharedComponents/Styles/CustomCardWithMetrics.styles";
import { arrSortAscByKeyValue } from "../../../../Utils/js-common";

interface DataPoint {
    x: string;
    y: number;
    aggregationType?: string;
}

interface MetricLine {
    name: string;
    data: DataPoint[];
}

interface DailyChartWrapperProps {
    metricsData: MetricLine[];
    allMetricsData?: MetricLine[]; // All metrics data (both tenant and non-tenant) for color index calculation
    width: number;
    height?: number;
}

/**
 * Reusable component for displaying daily chart data with month navigation
 * Displays one month of data at a time with left/right arrows for navigation
 */
export const DailyChartWrapper: React.FC<DailyChartWrapperProps> = ({
    metricsData,
    allMetricsData,
    width,
    height = 400
}) => {
    const [currentDate, setCurrentDate] = useState<Date | null>(null);

    // Reset to latest data date when metricsData changes
    useEffect(() => {
        // Find the most recent date in the data
        const allDates: Date[] = [];
        metricsData.forEach(metric => {
            metric.data.forEach(point => {
                if (point.aggregationType?.toLowerCase() === "daily" && point.x.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    allDates.push(new Date(point.x + "T00:00:00.000Z"));
                }
            });
        });

        if (allDates.length > 0) {
            const latestDate = new Date(Math.max(...allDates.map(d => d.getTime())));
            setCurrentDate(latestDate);
        } else {
            setCurrentDate(new Date());
        }
    }, [metricsData]);

    const renderDailyChart = () => {
        // Build a consistent legend order to keep colors matching monthly view
        // Use same sorting as ProductView.common.ts getMetricsData
        // Combine all metrics from all charts (both tenant and non-tenant) just like monthly view
        const dataForColorIndex = allMetricsData || metricsData;
        const allMetricNames = dataForColorIndex.map(m => ({ name: m.name }));
        const sortedMetrics = arrSortAscByKeyValue({ key: "name", data: allMetricNames });
        const legendOrder = sortedMetrics.map((m: any) => m.name);

        // Helper function to parse dates - handles both actual dates and month labels
        const parseDailyDates = (points: DataPoint[]) => {
            return points.map(point => {
                let date: Date;

                // Check if x is already in date format (yyyy-MM-dd)
                if (point.x.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    // Already a proper date string (ISO format)
                    date = new Date(point.x + "T00:00:00.000Z"); // Ensure UTC to avoid timezone issues
                } else {
                    // Legacy format: month label (e.g., "Jun 2025")
                    // This is for backward compatibility with old API responses
                    // Parse as "MMM yyyy" format
                    const monthDate = new Date(point.x + " 01"); // Add day to make valid date
                    if (isNaN(monthDate.getTime())) {
                        console.error(`[DailyChartWrapper] Invalid date format: ${point.x}`);
                        date = new Date(); // Fallback to current date
                    } else {
                        date = monthDate;
                    }
                }

                return { x: date, y: Number(point.y), originalX: point.x };
            });
        };

        // Filter and transform data for Daily aggregation
        const filteredLines = metricsData.map((metric) => {
            // First, filter to get only Daily aggregation type
            const dailyRawPoints = metric.data.filter(point => {
                // Strict comparison - only "Daily" aggregation type
                return point.aggregationType?.toLowerCase() === "daily";
            });

            // If no daily points, return empty
            if (dailyRawPoints.length === 0) {
                return {
                    legend: metric.name,
                    data: [],
                    color: getNextColor(legendOrder.findIndex(n => n === metric.name)),
                    lineOptions: { lineBorderWidth: "4" }
                };
            }

            // Check if we have actual dates or month labels
            const firstPoint = dailyRawPoints[0];
            const hasActualDates = firstPoint && firstPoint.x.match(/^\d{4}-\d{2}-\d{2}$/);

            let dailyPoints: Array<{ x: Date; y: number }>;

            if (hasActualDates) {
                // New API format: actual dates
                dailyPoints = parseDailyDates(dailyRawPoints)
                    .map(({ x, y }) => ({ x, y }))
                    .sort((a, b) => a.x.getTime() - b.x.getTime());
            } else {
                // Legacy format: month labels - generate sequential dates
                const grouped: { [key: string]: DataPoint[] } = {};

                dailyRawPoints.forEach(point => {
                    const monthKey = point.x;
                    if (!grouped[monthKey]) {
                        grouped[monthKey] = [];
                    }
                    grouped[monthKey].push(point);
                });

                dailyPoints = [];
                Object.keys(grouped).sort().forEach(monthKey => {
                    // Parse month label with added day to create valid date
                    const monthDate = new Date(monthKey + " 01");
                    if (isNaN(monthDate.getTime())) {
                        console.error(`[DailyChartWrapper] Invalid month label: ${monthKey}`);
                        return;
                    }

                    grouped[monthKey].forEach((point, dayIndex) => {
                        const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), dayIndex + 1);
                        dailyPoints.push({ x: date, y: Number(point.y) });
                    });
                });

                dailyPoints.sort((a, b) => a.x.getTime() - b.x.getTime());
            }

            return {
                legend: metric.name,
                data: dailyPoints,
                color: getNextColor(legendOrder.findIndex(n => n === metric.name)),
                lineOptions: {
                    lineBorderWidth: "4"
                }
            };
        });

        // Get all data points for navigation logic
        const allDataPoints = filteredLines.flatMap(line => line.data);

        if (allDataPoints.length === 0) {
            return (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height }}>
                    No daily data available
                </div>
            );
        }

        // Find valid data window (30 days) around current date
        const getValidDataWindow = (centerDate: Date | null) => {
            if (!centerDate) {
                return {
                    visibleLines: filteredLines.map(line => ({ ...line, data: [] })),
                    windowStart: new Date(),
                    windowEnd: new Date()
                };
            }

            const attemptDate = new Date(centerDate.getTime());

            // Try up to 6 months back to find data
            for (let i = 0; i < 6; i++) {
                const windowStart = new Date(attemptDate.getTime());
                windowStart.setMonth(windowStart.getMonth() - 1);
                windowStart.setDate(Math.min(windowStart.getDate(), 30));
                const windowEnd = new Date(attemptDate.getTime());

                const pointsInWindow = allDataPoints.filter(
                    p => p.x >= windowStart && p.x <= windowEnd
                );

                if (pointsInWindow.length > 0) {
                    const linesWithData = filteredLines.map(line => ({
                        ...line,
                        data: line.data.filter(p => p.x >= windowStart && p.x <= windowEnd)
                    }));

                    return { visibleLines: linesWithData, windowStart, windowEnd };
                }

                attemptDate.setDate(attemptDate.getDate() - 30);
            }

            // No data found, return empty
            return {
                visibleLines: filteredLines.map(line => ({ ...line, data: [] })),
                windowStart: new Date(centerDate.getTime()),
                windowEnd: new Date(centerDate.getTime())
            };
        };

        const { visibleLines, windowStart, windowEnd } = getValidDataWindow(currentDate);

        const hasNext = allDataPoints.some(p => p.x > windowEnd);
        const hasPrevious = allDataPoints.some(p => p.x < windowStart);

        const getAllTickValues = () => {
            const allPoints = visibleLines.flatMap(line => line.data);
            const uniqueTicks = Array.from(new Set(allPoints.map(p => p.x.getTime())))
                .map(timestamp => new Date(timestamp))
                .sort((a, b) => a.getTime() - b.getTime());

            // Show every 3rd day (0, 3, 6, 9, etc.)
            return uniqueTicks.filter((_, index) => index % 3 === 0);
        };

        const tickValues = getAllTickValues();
        const chartData: IChartProps = { lineChartData: visibleLines };

        return (
            <>
                <LineChartComponent
                    data={chartData}
                    allowResize={true}
                    tickValues={tickValues}
                    height={height}
                    width={width}
                    granularity="Daily"
                />
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: 12,
                        gap: 10
                    }}
                >
                    <IconButton
                        iconProps={{ iconName: "ChevronLeft" }}
                        title="Previous"
                        onClick={() =>
                            setCurrentDate(prev => {
                                if (!prev) return prev;
                                const newDate = new Date(prev.getTime());
                                newDate.setDate(newDate.getDate() - 30);
                                return newDate;
                            })
                        }
                        disabled={!hasPrevious}
                        styles={iconButtonStyles}
                    />
                    <IconButton
                        iconProps={{ iconName: "ChevronRight" }}
                        title="Next"
                        onClick={() =>
                            setCurrentDate(prev => {
                                if (!prev) return prev;
                                const newDate = new Date(prev.getTime());
                                newDate.setDate(newDate.getDate() + 30);
                                return newDate;
                            })
                        }
                        disabled={!hasNext}
                        styles={iconButtonStyles}
                    />
                </div>
            </>
        );
    };

    return renderDailyChart();
};
